import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search, Loader2, Users, Phone, Mail, MapPin, Calendar, Copy, Link2,
  CheckCircle, XCircle, Clock, UserCheck, MessageCircle, ChevronRight,
  ShieldAlert, Check,
} from "lucide-react";

/**
 * DecoratorRegistrationsTab — Admin tab for the public Decorator Registration
 * Portal. Lists applications from `decorator_registrations` with search,
 * status filter, detail drawer, internal notes and an explicit
 * "Approve & Convert to Vendor" action.
 */

const PAGE_SIZE = 50;
const PUBLIC_REGISTRATION_PATH = "/decorator-registration";

const STATUS_META = {
  new: { label: "New", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Clock },
  under_review: { label: "Under Review", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  contacted: { label: "Contacted", color: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: MessageCircle },
  approved: { label: "Approved", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
};
const STATUS_ORDER = ["new", "under_review", "contacted", "approved", "rejected"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const DecoratorRegistrationsTab = ({ auth, authAxios }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: PAGE_SIZE };
      if (debouncedSearch) params.q = debouncedSearch;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const res = await authAxios.get("/api/admin/decorator-registrations", { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setStatusCounts(res.data.status_counts || {});
    } catch (err) {
      if (err?.response?.status === 403) {
        setError("You don't have permission to view decorator registrations.");
      } else {
        setError(err?.response?.data?.detail || "Failed to load applications.");
      }
    } finally {
      setLoading(false);
    }
  }, [authAxios, debouncedSearch, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const publicUrl = useMemo(() => {
    if (typeof window === "undefined") return PUBLIC_REGISTRATION_PATH;
    return `${window.location.origin}${PUBLIC_REGISTRATION_PATH}`;
  }, []);

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1500);
    } catch { /* ignore */ }
  };

  if (error && items.length === 0 && !loading) {
    return (
      <div className="bg-white rounded-2xl p-10 border border-red-100 text-center" data-testid="decorator-registrations-forbidden">
        <ShieldAlert className="mx-auto mb-3 text-red-500" size={36} />
        <h2 className="text-xl font-bold text-[#1E1B4B] mb-1">Access denied</h2>
        <p className="text-sm text-[#64748B]">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="decorator-registrations-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#1E1B4B] flex items-center gap-2">
            <Users className="text-[#DC283C]" size={22} /> Decorator Registrations
          </h1>
          <p className="text-sm text-[#64748B] mt-1 max-w-xl">
            Public applications from the Decorator Registration Portal. Review and explicitly
            convert approved applicants into pending vendors — nothing goes live automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyPublicLink}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-[#1E1B4B] hover:bg-gray-50 transition"
            data-testid="decorator-copy-public-link"
            title={publicUrl}
          >
            {copiedLink ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
            {copiedLink ? "Link copied!" : "Copy Registration Link"}
          </button>
          <a
            href={PUBLIC_REGISTRATION_PATH}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#1E1B4B] text-white text-sm font-semibold hover:bg-[#2a2560] transition"
            data-testid="decorator-open-public-page"
          >
            <Link2 size={14} /> Open page
          </a>
        </div>
      </div>

      {/* Status chips + filter */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => { setStatusFilter("all"); setPage(1); }}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${statusFilter === "all" ? "bg-[#DC283C] text-white border-[#DC283C]" : "bg-white text-[#1E1B4B] border-gray-200 hover:bg-gray-50"}`}
          data-testid="decorator-filter-all"
        >
          All ({total})
        </button>
        {STATUS_ORDER.map((s) => {
          const meta = STATUS_META[s];
          const count = statusCounts[s] || 0;
          const active = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? "bg-[#DC283C] text-white border-[#DC283C]" : `${meta.color} hover:brightness-95`}`}
              data-testid={`decorator-filter-${s}`}
            >
              {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name, mobile, email, ID or pincode…"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none text-sm"
          data-testid="decorator-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs uppercase text-[#64748B]">
                <th className="text-left px-4 py-3 font-semibold">Application ID</th>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Contact</th>
                <th className="text-left px-4 py-3 font-semibold">Experience</th>
                <th className="text-left px-4 py-3 font-semibold">Pincodes</th>
                <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#64748B]"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-[#64748B]" data-testid="decorator-empty-state">No applications found.</td></tr>
              ) : items.map((it) => {
                const meta = STATUS_META[it.status] || STATUS_META.new;
                const Icon = meta.icon;
                return (
                  <tr key={it.application_id} className="border-t border-gray-100 hover:bg-pink-50/40 transition" data-testid={`decorator-row-${it.application_id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1E1B4B]">{it.application_id}</td>
                    <td className="px-4 py-3 font-semibold text-[#1E1B4B]">{it.name}</td>
                    <td className="px-4 py-3 text-xs text-[#1E1B4B]">
                      <div className="flex items-center gap-1"><Phone size={11} className="text-[#DC283C]" /> +91 {it.phone}</div>
                      {it.email && <div className="flex items-center gap-1 text-[#64748B] mt-0.5"><Mail size={11} /> {it.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#1E1B4B]">{it.experience_years} yr{it.experience_years === 1 ? "" : "s"}</td>
                    <td className="px-4 py-3 text-xs text-[#64748B]">
                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                        {(it.pincodes || []).slice(0, 3).map(p => (
                          <span key={p} className="px-1.5 py-0.5 rounded bg-gray-100 font-mono text-[10px]">{p}</span>
                        ))}
                        {(it.pincodes || []).length > 3 && <span className="text-[10px]">+{it.pincodes.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#64748B]"><Calendar size={11} className="inline mr-1" />{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${meta.color}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelected(it.application_id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#DC283C] text-white text-xs font-semibold hover:bg-[#d1366f] transition"
                        data-testid={`decorator-view-${it.application_id}`}
                      >
                        View <ChevronRight size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-[#64748B]">
            <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= total}
                className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <DetailDrawer
          applicationId={selected}
          authAxios={authAxios}
          onClose={() => setSelected(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
};

// ─── Detail Drawer ──────────────────────────────────────────────────────────
const DetailDrawer = ({ applicationId, authAxios, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState(null);
  const [feedback, setFeedback] = useState("");

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/api/admin/decorator-registrations/${applicationId}`);
      setData(res.data);
    } catch (err) {
      setFeedback(err?.response?.data?.detail || "Failed to load application details.");
    } finally {
      setLoading(false);
    }
  }, [authAxios, applicationId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const changeStatus = async (newStatus) => {
    if (saving) return;
    setSaving(true);
    setFeedback("");
    try {
      const res = await authAxios.patch(`/api/admin/decorator-registrations/${applicationId}`, {
        status: newStatus,
      });
      setData(res.data);
      onChanged();
      setFeedback(`Status updated to ${newStatus.replace("_", " ")}.`);
    } catch (err) {
      setFeedback(err?.response?.data?.detail || "Failed to update status.");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    const text = note.trim();
    if (!text || saving) return;
    setSaving(true);
    setFeedback("");
    try {
      const res = await authAxios.patch(`/api/admin/decorator-registrations/${applicationId}`, {
        admin_note: text,
      });
      setData(res.data);
      setNote("");
      setFeedback("Note added.");
    } catch (err) {
      setFeedback(err?.response?.data?.detail || "Failed to add note.");
    } finally {
      setSaving(false);
    }
  };

  const convertToVendor = async () => {
    if (converting) return;
    if (!window.confirm("Approve this application and create a pending vendor profile? The vendor will still require document verification before going live.")) return;
    setConverting(true);
    setFeedback("");
    try {
      const res = await authAxios.post(`/api/admin/decorator-registrations/${applicationId}/convert`);
      setConvertResult(res.data);
      onChanged();
      fetchDetail();
    } catch (err) {
      setFeedback(err?.response?.data?.detail || "Failed to convert application.");
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="decorator-detail-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#1E1B4B]">Application Details</h2>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100" data-testid="decorator-detail-close">
            <XCircle size={18} className="text-[#64748B]" />
          </button>
        </div>

        {loading || !data ? (
          <div className="flex items-center justify-center h-64 text-[#64748B]">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="bg-gradient-to-br from-pink-50 to-amber-50 rounded-2xl p-4 border border-pink-100">
              <p className="text-[11px] uppercase text-[#64748B] font-semibold">Application ID</p>
              <p className="text-xl font-bold text-[#DC283C] font-mono mt-0.5" data-testid="decorator-detail-id">{data.application_id}</p>
              <p className="text-[11px] text-[#64748B] mt-2">Submitted {fmtDate(data.created_at)}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Name" value={data.name} />
              <Field label="Mobile" value={`+91 ${data.phone}`} />
              <Field label="WhatsApp" value={data.whatsapp_phone ? `+91 ${data.whatsapp_phone}` : "—"} />
              <Field label="Email" value={data.email || "—"} />
              <Field label="Experience" value={`${data.experience_years} year${data.experience_years === 1 ? "" : "s"}`} />
              <Field label="Current Status" value={STATUS_META[data.status]?.label || data.status} />
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-[#64748B] mb-2">Pincodes ({(data.pincodes || []).length})</p>
              <div className="flex flex-wrap gap-1.5">
                {(data.pincodes || []).map(p => (
                  <span key={p} className="px-2 py-0.5 rounded bg-gray-100 text-xs font-mono text-[#1E1B4B]">{p}</span>
                ))}
              </div>
            </div>

            {data.converted_vendor_id && (
              <div className="px-3 py-2.5 rounded-xl bg-green-50 border border-green-100 text-sm text-green-800 flex items-center gap-2" data-testid="decorator-converted-banner">
                <UserCheck size={16} /> Converted to vendor <span className="font-mono font-semibold">{data.converted_vendor_id}</span>
              </div>
            )}

            {/* Status actions */}
            <div>
              <p className="text-xs font-semibold uppercase text-[#64748B] mb-2">Change Status</p>
              <div className="flex flex-wrap gap-2">
                {STATUS_ORDER.map(s => (
                  <button
                    key={s}
                    disabled={saving || data.status === s || data.converted_vendor_id}
                    onClick={() => changeStatus(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${data.status === s ? "bg-[#DC283C] text-white border-[#DC283C]" : STATUS_META[s].color + " hover:brightness-95"} disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid={`decorator-status-btn-${s}`}
                  >
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>
            </div>

            {/* Convert to vendor */}
            {!data.converted_vendor_id && (
              <div className="pt-3 border-t border-gray-100">
                <button
                  onClick={convertToVendor}
                  disabled={converting}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#DC283C] to-[#FF9F1C] text-white text-sm font-bold hover:shadow-lg disabled:opacity-60 transition flex items-center justify-center gap-2"
                  data-testid="decorator-convert-btn"
                >
                  {converting ? <><Loader2 className="animate-spin" size={14} /> Converting…</> : <><UserCheck size={14} /> Approve & Convert to Vendor</>}
                </button>
                <p className="text-[11px] text-[#64748B] mt-2">
                  Creates a <strong>pending</strong> vendor profile. The applicant will still need document verification before going live.
                </p>
              </div>
            )}

            {convertResult && (
              <div className="px-3 py-2.5 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800" data-testid="decorator-convert-result">
                {convertResult.message}
              </div>
            )}

            {/* Internal notes */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase text-[#64748B] mb-2">Internal Notes ({(data.admin_notes || []).length})</p>
              <div className="space-y-2 max-h-56 overflow-y-auto mb-2">
                {(data.admin_notes || []).slice().reverse().map((n, i) => (
                  <div key={`note-${n.at || i}-${(n.text || "").slice(0, 24)}`} className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-[#1E1B4B]" data-testid={`decorator-note-${i}`}>
                    <p className="whitespace-pre-wrap">{n.text}</p>
                    <p className="text-[10px] text-[#64748B] mt-1">— {n.actor} • {fmtDate(n.at)}</p>
                  </div>
                ))}
                {(data.admin_notes || []).length === 0 && (
                  <p className="text-xs text-[#64748B] italic">No notes yet.</p>
                )}
              </div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add an internal note (visible only to admins)…"
                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[#DC283C] focus:ring-2 focus:ring-[#DC283C]/20 outline-none text-sm"
                rows={2}
                maxLength={1000}
                data-testid="decorator-note-input"
              />
              <button
                onClick={addNote}
                disabled={!note.trim() || saving}
                className="mt-2 px-4 py-2 rounded-xl bg-[#1E1B4B] text-white text-xs font-semibold hover:bg-[#2a2560] disabled:opacity-50 transition"
                data-testid="decorator-note-save"
              >
                Add note
              </button>
            </div>

            {/* Status history */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-semibold uppercase text-[#64748B] mb-2">Status History</p>
              <div className="space-y-1.5">
                {(data.status_history || []).slice().reverse().map((h, i) => (
                  <div key={`sh-${h.at || i}-${h.status}`} className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-[#1E1B4B]">{(STATUS_META[h.status]?.label) || h.status}</span>
                    <span className="text-[#64748B]">{h.actor} • {fmtDate(h.at)}</span>
                  </div>
                ))}
              </div>
            </div>

            {feedback && (
              <div className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-800" data-testid="decorator-detail-feedback">
                {feedback}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase text-[#64748B] font-semibold">{label}</p>
    <p className="text-sm text-[#1E1B4B] font-semibold mt-0.5 break-words">{value || "—"}</p>
  </div>
);

export default DecoratorRegistrationsTab;
